import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "../../components/form-controls/Button";
import Input from "../../components/form-controls/Input";
import Select from 'react-select'
import useModal from "../../hooks/useModal";
import { AccountService } from "../../services/AccountsService";
import { useCurrentAccount } from "../../hooks/useCurrentAccount";
import { AlertService } from "../../services/Alert";
import Papa from 'papaparse';
import InvalidFeedback from "../../components/InvalidFeedback";
import { DBClientsService } from "../../services/DBClientsService";

declare const bootstrap: any;

interface ModalDBAddProps {
    show: boolean;
    onRequestClose: (refresh: boolean) => void
}
function ModalDBAdd({ show, onRequestClose }: ModalDBAddProps) {

    const { account } = useCurrentAccount();

    const modalRef = useRef<HTMLDivElement>(null)

    useModal(modalRef, show)

    const [subAccounts, setSubAccounts] = useState([])
    const [fieldsCSVFile, setFieldsCSVFile] = useState([])

    const defaultValues = {
        name: '',
        sub_account_id: '',
        header_number: '',
        header_name: '',
        data_process: '',
        csv: '',
    }
    const { control, register, watch, setValue, handleSubmit, formState: { errors, isValid, isSubmitting, submitCount } } = useForm({
        mode: 'onChange',
        defaultValues
    });

    const csvFile: any = watch('csv')

    const submit = async (data) => {
        try {
            let form = new FormData()
            form.append('account_id', account.id)
            form.append('name', data.name)
            form.append('sub_account_id', data.sub_account_id)
            form.append('header_number', data.header_number)
            form.append('header_name', data.header_name)
            form.append('data_process', data.data_process)
            form.append('csv', csvFile[0])

            await DBClientsService.save(form)
            await onRequestClose(true)
            
            AlertService.toastSuccess('Se guardó correctamente la base de clientes')
            
        } catch (error) {
            AlertService.error('¡Error!', 'Ocurrió un error al guardar la base de cliente, intenta más tarde.')
        }
    }

    const getSubAccounts = async () => {
        const { data } = await AccountService.getSubaccounts(account.id)
        let subaccts = [{ value: '', label: 'Sin subcuenta' }, ...data.sub_accounts.map(a => ({ value: a.id, label: a.name }))];
        setSubAccounts(subaccts)
    }

    useEffect(() => {
        getSubAccounts()
    }, [])

    useEffect(() => {
        let file: File = csvFile[0];

        if (file && file.type !== 'text/csv') {
            AlertService.toastError('Solo se permiten archivo .csv')
            setValue('csv', '')

            return;
        }

        if (file) {
            Papa.parse(file, {
                delimiter: ',',
                header: true,
                complete: (results) => {
                    const headers = results.meta.fields
                    setFieldsCSVFile(headers.map( h => ({ value: h, label: h })))
                    setValue('header_name', '')
                    setValue('header_number', '')
                },
                error: () => {
                    AlertService.error('Error', 'Ocurrió un error al leer el archivo CSV, verifica su estructura e intenta nuevamente.')
                    setValue('csv', '')
                },
            })

        }
    }, [csvFile])


    return (
        <div className="modal fade" ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog " role="document">
                <form onSubmit={handleSubmit(submit)}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title" >Agregar base de clientes</h4>
                            <button type="button" className="btn-close" onClick={() => onRequestClose(false)}></button>
                        </div>
                        <div className="modal-body">

                            <div className="row ">
                                <div className="col-12">
                                    <div className="mb-3">
                                        <h5>Información general</h5>
                                        <p className="small">Asigna un nombre y subcuenta (opcional) a tu base de datos de clientes .</p>
                                    </div>
                                    <div className="mb-3">
                                        <Input label='Nombre de la base *' errors={errors.name} {...register('name', { required: true })} />
                                    </div>

                                    <div className='mb-3'>
                                        <label className="form-label" >Subcuenta <span className="form-label-secondary">(Opcional)</span></label>
                                        <Controller
                                            control={control}
                                            name='sub_account_id'
                                            rules={{ required: false }}
                                            render={
                                                ({
                                                    field: { onChange, value },
                                                    fieldState: { error }
                                                }) => {
                                                    return <Select
                                                        name="Subcuenta (opcional)"
                                                        options={subAccounts}
                                                        className={error ? 'form-select-custom is-invalid' : ''}
                                                        classNamePrefix='form-select-custom'
                                                        placeholder='Subcuenta'
                                                        onChange={(option) => onChange(option.value)}
                                                    />
                                                }
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <hr />
                            <div className="row ">
                                <div className="col-12">
                                    <div className="mb-3">
                                        <h5>Datos de la base</h5>
                                        <p className="small">Adjunta un archivo .csv con la información de tus clientes. Es fundamental que tu archivo este delimitado por comas (,) y contenga al menos dos columnas con el nombre del cliente y su número telefónico. Puedes descargar <a href="/assets/layout-db.csv" target="_blank" className="text-success fw-semibold">aquí <i className="bi-box-arrow-up-right"></i></a> un layout básico para cargar una base de datos de clientes.</p>
                                    </div>

                                    <div className="mb-3">
                                        <Input type='file' label='Archivo csv *' errors={errors.csv} {...register('csv', { required: true })} />
                                    </div>
                                </div>
                            </div>
                            <hr />

                            <div className="row">
                                <div className="col-12">
                                    <div className="mb-3">
                                        <h5>Asignación de columnas</h5>
                                        <p className="small">Selecciona las columnas que corresponden al nombre del cliente y su número de teléfono. Adicionalmente selecciona que acción realizar con la información extra que se encuentre en el archivo csv.</p>
                                    </div>

                                    <div className="mb-3">                                        
                                        <div className='mb-3'>
                                            <label className="form-label" >Nombre del cliente *</label>
                                            <Controller
                                                control={control}
                                                name='header_name'
                                                rules={{ required: true }}
                                                render={
                                                    ({
                                                        field: { onChange, value },
                                                        fieldState: { error }
                                                    }) => {

                                                        return (
                                                            <>
                                                                <Select
                                                                    name="Nombre del cliente"
                                                                    options={fieldsCSVFile}
                                                                    className={error ? 'form-select-custom is-invalid' : ''}
                                                                    classNamePrefix='form-select-custom'
                                                                    placeholder='Nombre del cliente'
                                                                    onChange={(option) => onChange(option.value)}
                                                                    value={ fieldsCSVFile.find( row => row.value === value) || '' }
                                                                />
                                                                <InvalidFeedback error={error} />
                                                            </>
                                                        )
                                                    }
                                                }
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">                                        
                                        <div className='mb-3'>
                                            <label className="form-label" >Número de teléfono *</label>
                                            <Controller
                                                control={control}
                                                name='header_number'
                                                rules={{ required: true }}
                                                render={
                                                    ({
                                                        field: { onChange, value },
                                                        fieldState: { error }
                                                    }) => {
                                                        return (
                                                            <>
                                                                <Select
                                                                    name="Número de teléfono"
                                                                    options={fieldsCSVFile}
                                                                    className={error ? 'form-select-custom is-invalid' : ''}
                                                                    classNamePrefix='form-select-custom'
                                                                    placeholder='Número de teléfono'
                                                                    onChange={(option) => onChange(option.value)}
                                                                    value={ fieldsCSVFile.find( row => row.value === value) || '' }
                                                                />
                                                                <InvalidFeedback error={error} />
                                                            </>
                                                        )
                                                    }
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className='mb-3'>
                                        <label className="form-label" >Procesar información adicional de CSV para</label>
                                        <Controller
                                            control={control}
                                            name='data_process'
                                            rules={{ required: true }}
                                            render={
                                                ({
                                                    field: { onChange, value },
                                                    fieldState: { error }
                                                }) => {

                                                    const options = [
                                                        {
                                                            label: 'Actualizar',
                                                            value: 'update',
                                                        },
                                                        {
                                                            label: 'Reemplazar',
                                                            value: 'replace',
                                                        },
                                                        {
                                                            label: 'Ignorar',
                                                            value: 'ignore',
                                                        },
                                                    ]
                                                    
                                                    return (
                                                        <>
                                                            <Select
                                                                name="Procesar información adicional de CSV"
                                                                options={options}
                                                                className={error ? 'form-select-custom is-invalid' : ''}
                                                                classNamePrefix='form-select-custom'
                                                                placeholder="Procesar información adicional de CSV"
                                                                onChange={(option: any) => onChange(option.value)}
                                                                value={options.find(row => row.value === value) || ''}
                                                            />
                                                            <InvalidFeedback error={error} />
                                                        </>
                                                    )
                                                }
                                            }
                                        />
                                    </div>


                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-white btn-sm" onClick={() => onRequestClose(false)}>Cancelar</button>
                            <Button type="submit" className="btn btn-primary btn-sm" disabled={!isValid} isLoading={isSubmitting}>Guardar</Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}
export default ModalDBAdd